import React, { useState, useCallback, useEffect, useRef } from 'react';
import './App.css';
import { SCENES } from './scenes';
import { preloadScene, preloadAllAssets, HiddenPrefetch, ALL_ASSETS } from './preloadAssets';
import LoadingScreen from './components/LoadingScreen';
import TitleScreen from './scenes/TitleScreen';
import OnboardingScreen from './scenes/OnboardingScreen';
import MainMapScreen from './scenes/MainMapScreen';
import FieldIntroScene from './scenes/FieldIntroScene';
import WorldMapScreen from './scenes/WorldMapScreen';
import FirebreakScene from './scenes/FirebreakScene';
import NativePlantsScene from './scenes/NativePlantsScene';
import GoatsScene from './scenes/GoatsScene';
import ControlledBurnScene from './scenes/ControlledBurnScene';
import VictoryScreen from './scenes/VictoryScreen';
import FireStationGame from './scenes/FireStationGame';
import HomeSafetyGame from './scenes/HomeSafetyGame';
import TownHallGame from './scenes/TownHallGame';
import WeatherWatchWoods from './scenes/WeatherWatchWoods';
import FinalCongratsScreen from './scenes/FinalCongratsScreen';
import SceneTransition from './components/SceneTransition';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ width: '100vw', height: '100vh', background: '#1a1a1a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: 'monospace', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>Error</div>
          <pre style={{ background: '#333', padding: 20, borderRadius: 8, fontSize: 13, maxWidth: '80vw', overflow: 'auto', color: '#ff6b6b', whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
          </pre>
          <button onClick={() => this.setState({ hasError: false, error: null })} style={{ marginTop: 20, padding: '10px 24px', background: '#F5C518', border: 'none', borderRadius: 12, fontSize: 16, cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function clearSavedProgress() {
  try {
    localStorage.removeItem('completedGames');
    localStorage.removeItem('completedLevels');
    localStorage.removeItem('hasSeenOnboarding');
  } catch (e) { /* ignore */ }
}

function App() {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.has('reset')) {
      clearSavedProgress();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  const [currentScene, setCurrentScene] = useState(SCENES.TITLE);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionColor, setTransitionColor] = useState('#2d5a1b');

  // Game 4 (Field) mini-game level completion
  const [completedLevels, setCompletedLevels] = useState(() => {
    try { const s = localStorage.getItem('completedLevels'); return s ? JSON.parse(s) : []; } catch { return []; }
  });

  // Main game (5 locations) completion
  const [completedGames, setCompletedGames] = useState(() => {
    try { const s = localStorage.getItem('completedGames'); return s ? JSON.parse(s) : []; } catch { return []; }
  });

  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(() => {
    try { return localStorage.getItem('hasSeenOnboarding') === 'true'; } catch { return false; }
  });

  const navTimers = useRef({ swap: null, reveal: null });
  const [loadProgress, setLoadProgress] = useState(0);
  const [assetsReady, setAssetsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      if (!cancelled) setAssetsReady(true);
    }, 20000);

    preloadAllAssets({
      onProgress: (done, total) => {
        if (!cancelled && total) setLoadProgress(done / total);
      },
    }).then(() => {
      if (!cancelled) {
        setLoadProgress(1);
        setAssetsReady(true);
      }
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, []);

  const navigateTo = useCallback((scene, color) => {
    preloadScene(scene);
    setTransitionColor(color || '#2d5a1b');
    setTransitioning(true);
    window.clearTimeout(navTimers.current.swap);
    window.clearTimeout(navTimers.current.reveal);
    // Swap the scene while the overlay is covering the screen so the next
    // images can decode before the player sees them next to Blaze's line.
    navTimers.current.swap = window.setTimeout(() => setCurrentScene(scene), 220);
    navTimers.current.reveal = window.setTimeout(() => setTransitioning(false), 400);
  }, []);

  const completeLevel = useCallback((levelName) => {
    setCompletedLevels(prev => {
      const updated = prev.includes(levelName) ? prev : [...prev, levelName];
      localStorage.setItem('completedLevels', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const completeGame = useCallback((gameId) => {
    setCompletedGames(prev => {
      const updated = prev.includes(gameId) ? prev : [...prev, gameId];
      localStorage.setItem('completedGames', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const completeOnboarding = useCallback(() => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setHasSeenOnboarding(true);
  }, []);

  const resetProgress = useCallback(() => {
    clearSavedProgress();
    setCompletedGames([]);
    setCompletedLevels([]);
    setHasSeenOnboarding(false);
    navigateTo(SCENES.TITLE);
  }, [navigateTo]);

  const props = { navigateTo, completedLevels, completeLevel, completedGames, completeGame, hasSeenOnboarding, completeOnboarding, resetProgress };

  const renderScene = () => {
    switch (currentScene) {
      // Top level
      case SCENES.TITLE:       return <TitleScreen {...props} />;
      case SCENES.ONBOARDING:  return <OnboardingScreen {...props} />;
      case SCENES.MAIN_MAP:    return <MainMapScreen {...props} />;

      // Game 4: Field / Defensible Space
      case SCENES.FIELD_INTRO: return <FieldIntroScene {...props} />;
      case SCENES.FIELD_MAP:   return <WorldMapScreen {...props} />;
      case SCENES.FIREBREAK:   return <FirebreakScene {...props} />;
      case SCENES.NATIVE_PLANTS: return <NativePlantsScene {...props} />;
      case SCENES.GOATS:       return <GoatsScene {...props} />;
      case SCENES.CONTROLLED_BURN: return <ControlledBurnScene {...props} />;
      case SCENES.FIELD_VICTORY: return <VictoryScreen {...props} />;

      // Placeholder games
      case SCENES.FIRE_STATION: return <FireStationGame {...props} />;
      case SCENES.HOME_SAFETY:  return <HomeSafetyGame {...props} />;
      case SCENES.TOWN_HALL:    return <TownHallGame {...props} />;
      case SCENES.WOODS:        return <WeatherWatchWoods {...props} />;
      case SCENES.FINAL_CONGRATS: return <FinalCongratsScreen {...props} />;

      default: return <TitleScreen {...props} />;
    }
  };

  return (
    <ErrorBoundary>
      <div className="app-container">
        <HiddenPrefetch urls={ALL_ASSETS} />
        {assetsReady ? renderScene() : <LoadingScreen progress={loadProgress} />}
        <SceneTransition active={transitioning} color={transitionColor} />
      </div>
    </ErrorBoundary>
  );
}

export default App;
